import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useMediaQuery } from "@mui/material";

import CommonBGDesktop from "@assets/avif/common-desktop.avif";
import CommonBGMobile from "@assets/avif/common-mobile.avif";

import { useIsDesktop } from "@hooks/layout";

import { ERoutes } from "@utils/constants";

import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { PageContainer } from "@/ui-kit";

import * as S from "./styles";

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const IM = ({ paths }: { paths: string[] }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {paths.map((d, i) => (
      <path key={i} d={d} />
    ))}
  </svg>
);

// Brand diamond (same shape as the gold ◆ on the pricing/subscription CTAs).
// Filled rather than stroked so it reads as the premium mark, not an outline.
const DiamondIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 3h12l4 6-10 13L2 9Z" /></svg>
);

const buildSections = (accountType?: AccountType): NavSection[] => [
  {
    title: "",
    items: [
      ...(accountType !== AccountType.MUSIC_CREATOR ? [{
        path: ERoutes.AccountSubscription,
        label: "Subscription",
        icon: <DiamondIcon />,
      }] : []),
      {
        path: ERoutes.Profile,
        label: "Account",
        icon: <IM paths={["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2", "M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"]} />,
      },
      // Orders shown for ALL plans incl. Music Creators (plan-logic-table §9:
      // "My Orders: Yes"). The header dropdown already shows it — the sidebar
      // was the diverging surface (M3).
      {
        path: ERoutes.AccountOrders,
        label: "Orders",
        icon: <IM paths={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6", "M16 13H8", "M16 17H8"]} />,
      },
      {
        path: ERoutes.AccountNotifications,
        label: "Notifications",
        icon: <IM paths={["M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9", "M13.73 21a2 2 0 0 1-3.46 0"]} />,
      },
      // Legal tab only for Music Creators (their partnership terms live here);
      // free / subscription accounts get legal links from the footer instead.
      ...(accountType === AccountType.MUSIC_CREATOR ? [{
        path: "/account/legal" as ERoutes,
        label: "Legal",
        icon: <IM paths={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6"]} />,
      }] : []),
    ],
  },
];

const AccountLayout: FC<{ children: React.ReactNode; title?: React.ReactNode; description?: React.ReactNode; bannerExtra?: React.ReactNode; headerExtra?: React.ReactNode;
  /** Sit the header extra immediately beside the title instead of pushing it to
      the far right — for tab switchers, which read as part of the heading. */
  headerExtraInline?: boolean; contentBanner?: React.ReactNode; noSidebar?: boolean; tintedHeader?: boolean }> = ({ children, title, description, bannerExtra, headerExtra, headerExtraInline = false, contentBanner, noSidebar = false, tintedHeader = false }) => {
  const location = useLocation();
  const isDesktop = useIsDesktop();
  // The sidebar only exists as a left rail at the 1150 header-swap boundary;
  // below that it's the horizontal pill menu, so the sticky-rail JS must NOT
  // run (it would pin the pill row as a fixed 220px column on scroll).
  const isDesktopLayout = useMediaQuery("(min-width:1150px)");
  const { accountType } = useAccountType();
  const sidebarRef = useRef<HTMLElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const sidebarTopRef = useRef<number | null>(null);
  // Horizontal pill scroller + the active pill (mobile/tablet only) — used to
  // scroll the current page's pill into view on load, like the FAQ tabs. Each
  // account page is its own route, so the scroller remounts at scrollLeft 0.
  const pillScrollerRef = useRef<HTMLUListElement>(null);
  const activePillRef = useRef<HTMLLIElement>(null);
  // Edge-fade affordance: when the pill row overflows, fade out the pills at
  // whichever edge has hidden content — so even when a pill ends exactly on the
  // viewport edge (no peeking sliver), there's still a "swipe for more" cue.
  const [pillFade, setPillFade] = useState<{ left: boolean; right: boolean }>({ left: false, right: false });

  const sections = buildSections(accountType);

  const isActive = (path: string) => {
    if (path === ERoutes.Profile) return location.pathname === ERoutes.Profile || location.pathname === "/account";
    return location.pathname.startsWith(path);
  };

  // Recompute which edges should fade based on the scroller's current overflow
  // + scroll position (2px slack absorbs sub-pixel rounding).
  const updatePillFade = useCallback(() => {
    const el = pillScrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const scrollable = max > 2;
    setPillFade({
      left: scrollable && el.scrollLeft > 2,
      right: scrollable && el.scrollLeft < max - 2,
    });
  }, []);

  // Bring the active pill into view without yanking the page: only scroll the
  // scroller (not the window) when the active pill sits past either edge. Mirror
  // of the FAQ tab-scroll logic. Then refresh the edge fades.
  useEffect(() => {
    if (isDesktopLayout) return;
    const scroller = pillScrollerRef.current;
    const pill = activePillRef.current;
    if (!scroller || !pill) return;
    const sc = scroller.getBoundingClientRect();
    const p = pill.getBoundingClientRect();
    const padLeft = parseFloat(getComputedStyle(scroller).paddingLeft) || 0;
    if (p.left < sc.left + padLeft) {
      scroller.scrollBy({ left: p.left - sc.left - padLeft, behavior: "smooth" });
    } else if (p.right > sc.right) {
      scroller.scrollBy({ left: p.right - sc.right + 8, behavior: "smooth" });
    }
    updatePillFade();
  }, [location.pathname, isDesktopLayout, updatePillFade]);

  // Keep the edge fades in sync with scrolling + viewport resizes.
  useEffect(() => {
    if (isDesktopLayout) {
      setPillFade({ left: false, right: false });
      return;
    }
    const el = pillScrollerRef.current;
    if (!el) return;
    updatePillFade();
    el.addEventListener("scroll", updatePillFade, { passive: true });
    window.addEventListener("resize", updatePillFade);
    return () => {
      el.removeEventListener("scroll", updatePillFade);
      window.removeEventListener("resize", updatePillFade);
    };
  }, [updatePillFade, isDesktopLayout, location.pathname]);

  const handleScroll = useCallback(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar || !isDesktopLayout) return;

    if (sidebarTopRef.current === null) {
      sidebarTopRef.current = sidebar.getBoundingClientRect().top + window.scrollY;
    }

    const placeholder = placeholderRef.current;
    if (window.scrollY >= sidebarTopRef.current) {
      // Site header stays pinned (static, no fade) on /account/* pages
      // with a sidebar, so the sticky sidebar can safely sit at top: 0
      // without colliding.
      sidebar.style.position = "fixed";
      sidebar.style.top = "0";
      sidebar.style.width = "220px";
      if (placeholder) placeholder.style.display = "block";
    } else {
      sidebar.style.position = "";
      sidebar.style.top = "";
      sidebar.style.width = "";
      if (placeholder) placeholder.style.display = "none";
    }
  }, [isDesktopLayout]);

  useEffect(() => {
    // Below the boundary the rail is a top pill menu — don't attach the sticky
    // handler, and clear any stale fixed-position inline styles left over from a
    // wider viewport (e.g. after a resize/orientation change across 1150).
    if (!isDesktopLayout) {
      const sidebar = sidebarRef.current;
      if (sidebar) {
        sidebar.style.position = "";
        sidebar.style.top = "";
        sidebar.style.width = "";
      }
      const placeholder = placeholderRef.current;
      if (placeholder) placeholder.style.display = "none";
      return;
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll, isDesktopLayout]);

  // Reset when navigating
  useEffect(() => {
    sidebarTopRef.current = null;
    const sidebar = sidebarRef.current;
    if (sidebar) {
      sidebar.style.position = "";
      sidebar.style.top = "";
      sidebar.style.width = "";
    }
    const placeholder = placeholderRef.current;
    if (placeholder) placeholder.style.display = "none";
  }, [location.pathname]);

  return (
    <PageContainer
      isPage
      backgroundImage={isDesktop ? CommonBGDesktop : CommonBGMobile}
      sx={{
        // /account/* pages with a sidebar keep the header in flow
        // (paddingTop: 0). /account/claims uses `noSidebar` and the
        // header is fixed there, so 80px clears the 72px header with
        // a small breathing gap (matches /music).
        "&&": {
          paddingTop: noSidebar ? "80px" : 0,
          minHeight: "100vh",
          // Below 1150 the MobileHeader is fixed but renders its own in-flow
          // spacer that reserves its height — so the 80px desktop fixed-header
          // clearance isn't needed and just leaves a big empty gap below the
          // (mobile-mounted) global banner. Collapse it to a small breathing gap.
          // Tinted-header pages (claims): the dark band must touch the site
          // header, so drop the top gap entirely on compact.
          "@media (max-width: 1149.98px)": { paddingTop: noSidebar ? (tintedHeader ? "0px" : "20px") : 0 },
        },
        "&& > div": { alignSelf: "start" },
      }}
      style={{
        // iOS Safari renders `background-attachment: fixed` with a 1px tearing
        // seam at sticky-element boundaries during scroll — disable it on
        // mobile/tablet (the sticky MobileHeader is what the seam appeared
        // under). Desktop keeps the fixed parallax.
        backgroundAttachment: isDesktop ? "fixed" : "scroll",
        maskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
      } as React.CSSProperties}
    >
      {/* Account banner removed — replaced by the site-wide
          <LoggedInBanner /> mounted in SecondRootLayout. The
          `bannerExtra` slot (dev switchers) is now rendered inside
          the title row alongside `headerExtra`. */}
      <S.AccountContainer>
        {!noSidebar && (
          <>
            <div ref={placeholderRef} style={{ display: "none", width: "220px", flexShrink: 0 }} />
            <S.Sidebar ref={sidebarRef}>
              {sections.map((section) => (
                <S.SidebarSection key={section.title}>
                  {section.title && <S.SidebarSectionTitle>{section.title}</S.SidebarSectionTitle>}
                  <S.SidebarItems
                    ref={pillScrollerRef}
                    style={(() => {
                      // Soft fade on the edge(s) that still have hidden pills —
                      // the "swipe for more" cue. No fade when nothing overflows.
                      if (!pillFade.left && !pillFade.right) return undefined;
                      // Same ~28px width, but a steeper ramp that reaches FULLY
                      // transparent ~8px before the edge → a stronger fade.
                      const left = pillFade.left ? "transparent 0, transparent 8px, #000 28px" : "#000 0";
                      const right = pillFade.right ? "#000 calc(100% - 28px), transparent calc(100% - 8px), transparent 100%" : "#000 100%";
                      const mask = `linear-gradient(to right, ${left}, ${right})`;
                      return { maskImage: mask, WebkitMaskImage: mask } as React.CSSProperties;
                    })()}
                  >
                    {section.items.map((item) => (
                      <S.SidebarItem
                        key={item.path}
                        $active={isActive(item.path)}
                        ref={isActive(item.path) ? activePillRef : undefined}
                      >
                        <Link to={item.path}>
                          {item.icon}
                          {item.label}
                          {item.badge != null && item.badge > 0 && (
                            <S.SidebarBadge>{item.badge}</S.SidebarBadge>
                          )}
                        </Link>
                      </S.SidebarItem>
                    ))}
                  </S.SidebarItems>
                </S.SidebarSection>
              ))}
            </S.Sidebar>
          </>
        )}

        <S.ContentArea $tintedHeader={tintedHeader} style={noSidebar ? { paddingLeft: 0 } : undefined}>
          {(() => {
          const headerInner = (
            <>
          {contentBanner}
          {title && (
            <S.TitleBlock $tinted={tintedHeader}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: headerExtraInline ? "flex-start" : "space-between", gap: headerExtraInline ? 20 : 16, flexWrap: "wrap" }}>
                <S.ContentTitle style={{ margin: 0 }}>{title}</S.ContentTitle>
                {headerExtra}
              </div>
              {description && (
                <div style={{
                  fontFamily: '"Raleway", sans-serif',
                  fontSize: "0.9375rem",
                  color: "rgba(251, 244, 236, 0.7)",
                  lineHeight: 1.55,
                  marginTop: "10px",
                  paddingLeft: "5px",
                }}>
                  {description}
                </div>
              )}
              {/* DEV switchers (was inline next to the title; moved here
                  so they sit below the page description). Production
                  builds pass nothing through `bannerExtra`. */}
              {bannerExtra && (
                <div style={{
                  marginTop: "14px",
                  paddingLeft: "5px",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                  alignItems: "center",
                }}>
                  {bannerExtra}
                </div>
              )}
            </S.TitleBlock>
          )}
            </>
          );
          return tintedHeader ? <S.HeaderBandOuter>{headerInner}</S.HeaderBandOuter> : headerInner;
          })()}

          {children}
        </S.ContentArea>
      </S.AccountContainer>
    </PageContainer>
  );
};

export default AccountLayout;
